import { cn } from '@/shared/lib/cn';
import { Typography } from '@/shared/ui';
import type { FC } from 'react';

type MonthWindowPagerProps = {
  label: string;
  canGoBack: boolean;
  canGoForward: boolean;
  onBack: () => void;
  onForward: () => void;
};

const arrowClass = (enabled: boolean) =>
  cn(
    'flex h-8 w-8 items-center justify-center leading-none',
    enabled
      ? 'text-neutral-8 hover:text-neutral-11 cursor-pointer'
      : // Lighter grey at the bounds, per the card text — disabled is the state,
        // the colour is only how it reads.
        'text-neutral-5 cursor-default'
  );

/**
 * The chart's own window control: `‹ Last 12 months ›`, one month per click.
 *
 * It is not a date picker — the window is always 12 months (or all there is),
 * so there is nothing to pick, only somewhere to move.
 */
export const MonthWindowPager: FC<MonthWindowPagerProps> = ({
  label,
  canGoBack,
  canGoForward,
  onBack,
  onForward,
}) => (
  <div className="border-neutral-5 flex items-center gap-1 border px-1">
    <button
      type="button"
      aria-label="Previous month"
      disabled={!canGoBack}
      className={arrowClass(canGoBack)}
      onClick={onBack}
    >
      ‹
    </button>

    <Typography
      variant="body"
      data-testid="month-window-label"
      className="text-neutral-11 min-w-[9rem] text-center"
    >
      {label}
    </Typography>

    <button
      type="button"
      aria-label="Next month"
      disabled={!canGoForward}
      className={arrowClass(canGoForward)}
      onClick={onForward}
    >
      ›
    </button>
  </div>
);
